import { mainAPIUrl } from '../../../settings';

const baseURL = `${mainAPIUrl}/gservices_3/ss`;

/**
 * Send SMS verification code to the user's phone
 * Endpoint: POST /gservices_3/ss/verify
 * Uses the same verification system as GRunner signup and SmartSend v1
 *
 * @param {string} phoneNumber - Phone number in E.164 format (e.g., "+14155552671")
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const sendVerificationCode = async (phoneNumber) => {
  try {
    const url = `${baseURL}/verify`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ phoneNumber })
    });

    const json = await response.json();

    if (response.ok && json.code === 'SUCCESS') {
      return {
        success: true
      };
    } else if (json.code === 'TWILIO_ERROR') {
      return {
        success: false,
        error: 'Please verify that the number you have entered is valid and try again.'
      };
    } else {
      return {
        success: false,
        error: json.message || 'Failed to send verification code. Please try again.'
      };
    }
  } catch (error) {
    console.error('Error sending verification code:', error);
    return {
      success: false,
      error: 'Network error. Please try again later.'
    };
  }
};

/**
 * Format phone number to E.164 format
 * Helper function to ensure consistent phone number format
 * @param {string} phoneNumber - Phone number in any format
 * @returns {string} Phone number in E.164 format
 */
export const formatPhoneToE164 = (phoneNumber) => {
  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');

  // If it starts with 1 and has 11 digits, return with +
  if (digits.length === 11 && digits[0] === '1') {
    return `+${digits}`;
  }

  // If it has 10 digits, prepend +1
  if (digits.length === 10) {
    return `+1${digits}`;
  }

  // Already has country code, just add +
  if (digits.length > 10) {
    return `+${digits}`;
  }

  // Return as is if invalid length
  return phoneNumber;
};

/**
 * Validate US/CA phone number
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} True if valid
 */
export const isValidPhoneNumber = (phoneNumber) => {
  const digits = phoneNumber.replace(/\D/g, '');

  // US/CA phone numbers are 10 digits (or 11 with country code)
  if (digits.length === 10) return true;
  if (digits.length === 11 && digits[0] === '1') return true;

  return false;
};
